<template>
  <div id="family-groups" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading family groups..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-dialog
        v-if="familyGroupDialog"
        v-model="familyGroupDialog"
        max-width="850px"
        persistent
        @click:outside="reset"
        @keydown.esc="reset"
      >
        <i-edit-family-group
          :family-group="editableFamilyGroup"
          :family-groups="familyGroups"
          :products="products"
          @cancel="reset"
          @save="reload"
        ></i-edit-family-group>
      </v-dialog>

      <v-dialog
        v-if="deletionDialog"
        v-model="deletionDialog"
        max-width="400px"
        persistent
        @click:outside="reset"
        @keydown.esc="reset"
      >
        <i-delete-family-group
          :familyGroup="selectedFamilyGroup"
          :loading="deletingFamilyGroup"
          @cancel="reset"
          @confirm="deleteFamilyGroup(selectedFamilyGroup)"
        ></i-delete-family-group>
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row>
          <v-col cols="12" md="3" lg="2">
            <v-btn
              large
              rounded
              class="i-btn-block-secondary g-full-width"
              @click="editFamilyGroup()"
            >
              Add Family Group
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="data-maintenance/family-groups"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <i-local-filter
              :filterPlaceholder="'Search Family Group by Name'"
              @filter="localFilter"
            />
          </v-col>
        </v-row>
        <template>
          <v-row>
            <v-col cols="12">
              <template
                v-if="!loading && filteredEnrichedFamilyGroups.length === 0"
              >
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <v-col
                    v-for="item in filteredEnrichedFamilyGroups"
                    :key="item.id"
                    class="mt-0 pa-0"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-img
                            contain
                            class="family-group-image"
                            :src="item.imageUrl"
                          ></v-img>
                        </v-col>

                        <v-col cols="6">
                          <span><strong>Family Group Name</strong></span
                          ><br />
                          <span>{{ item.name }}</span>
                        </v-col>
                      </v-row>

                      <v-divider class="my-3"></v-divider>

                      <p
                        v-if="item.status === 'LIVE'"
                        class="g-border-radius-s g-bold i-status-block-green py-2 text-center"
                      >
                        STATUS: ACTIVE
                      </p>
                      <p
                        v-else
                        class="g-border-radius-s g-bold i-status-block-red py-2 text-center"
                      >
                        STATUS: INACTIVE
                      </p>

                      <v-divider class="my-3"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="12" class="py-1">
                          <v-btn
                            class="i-btn-actions-blue g-full-width"
                            @click="editFamilyGroup(item)"
                          >
                            <span>Edit Family Group</span>
                            <v-icon small class="ml-1"
                              >mdi-circle-edit-outline</v-icon
                            >
                          </v-btn>
                        </v-col>
                        <v-col cols="12" class="py-1">
                          <v-btn
                            class="i-btn-actions-red g-full-width"
                            @click="startDeletion(item)"
                          >
                            <span>Delete Family Group</span>
                            <v-icon small class="ml-1"
                              >mdi-delete-outline</v-icon
                            >
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-divider class="my-3"></v-divider>

                      <v-btn
                        rounded
                        class="g-full-width i-btn-block-primary"
                        @click="item.showDetails = !item.showDetails"
                      >
                        <template v-if="item.showDetails"
                          ><v-icon left class="mt-1">mdi-arrow-up</v-icon> Hide
                          details</template
                        >
                        <template v-else
                          ><v-icon left class="mt-1">mdi-arrow-down</v-icon>
                          Show details</template
                        >
                      </v-btn>

                      <v-row
                        class="family-group-details mx-2 mx-sm-4 mt-4"
                        v-if="item.showDetails"
                        no-gutters
                      >
                        <v-col cols="12">
                          <div class="pb-2 g-heading-xs g-bold">
                            Products in Family Group
                          </div>
                          <div class="mb-4">
                            <v-chip
                              outlined
                              dense
                              small
                              class="mr-1 mb-2"
                              color="primary"
                              v-for="product in item.products"
                              :key="product.productCode"
                            >
                              {{ product.tradeItemDescription }}
                            </v-chip>
                          </div>
                        </v-col>

                        <v-col cols="12">
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFuturePriceDrops,
                                'ACTIVE'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Active Price Drops
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="priceDrop in promoByStatus(
                                  item.activeOrFuturePriceDrops,
                                  'ACTIVE'
                                )"
                                :key="priceDrop.id"
                              >
                                <router-link
                                  :to="{
                                    name: 'price-drops',
                                    query: { id: priceDrop.id }
                                  }"
                                  class="i-pill-link"
                                  >{{ priceDrop.startDate }} ~
                                  {{ priceDrop.endDate }}
                                </router-link>
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFuturePriceDrops,
                                'UPCOMING'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Upcoming Price Drops
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="priceDrop in promoByStatus(
                                  item.activeOrFuturePriceDrops,
                                  'UPCOMING'
                                )"
                                :key="priceDrop.id"
                              >
                                <router-link
                                  :to="{
                                    name: 'price-drops',
                                    query: { id: priceDrop.id }
                                  }"
                                  class="i-pill-link"
                                  >{{ priceDrop.startDate }} ~
                                  {{ priceDrop.endDate }}
                                </router-link>
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFuturePromotions,
                                'ACTIVE'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Active Specials
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="special in promoByStatus(
                                  item.activeOrFuturePromotions,
                                  'ACTIVE'
                                )"
                                :key="special.id"
                              >
                                {{ special.startDate }} ~ {{ special.endDate }}
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFuturePromotions,
                                'UPCOMING'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Upcoming Specials
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="special in promoByStatus(
                                  item.activeOrFuturePromotions,
                                  'UPCOMING'
                                )"
                                :key="special.id"
                              >
                                {{ special.startDate }} ~ {{ special.endDate }}
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFutureVolumePromotions,
                                'ACTIVE'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Active Volume Buys
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="volumeBuy in promoByStatus(
                                  item.activeOrFutureVolumePromotions,
                                  'ACTIVE'
                                )"
                                :key="volumeBuy.id"
                              >
                                {{ volumeBuy.name }}
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFutureVolumePromotions,
                                'UPCOMING'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Upcoming Volume Buys
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="volumeBuy in promoByStatus(
                                  item.activeOrFutureVolumePromotions,
                                  'UPCOMING'
                                )"
                                :key="volumeBuy.id"
                              >
                                {{ volumeBuy.name }}
                              </v-chip>
                            </div>
                          </template>
                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFutureBuyGetFreePromotions,
                                'ACTIVE'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Active Buy Get Free
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="buyGet in promoByStatus(
                                  item.activeOrFutureBuyGetFreePromotions,
                                  'ACTIVE'
                                )"
                                :key="buyGet.id"
                              >
                                <router-link
                                  :to="{
                                    name: 'buy-get-free',
                                    query: { id: buyGet.id }
                                  }"
                                  class="i-pill-link"
                                >
                                  {{ buyGet.name }}
                                </router-link>
                              </v-chip>
                            </div>
                          </template>

                          <template
                            v-if="
                              promoByStatus(
                                item.activeOrFutureBuyGetFreePromotions,
                                'UPCOMING'
                              ).length
                            "
                          >
                            <div class="pb-2 g-heading-xs g-bold">
                              Upcoming Buy Get Free
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                small
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="buyGet in promoByStatus(
                                  item.activeOrFutureBuyGetFreePromotions,
                                  'UPCOMING'
                                )"
                                :key="buyGet.id"
                              >
                                <router-link
                                  :to="{
                                    name: 'buy-get-free',
                                    query: { id: buyGet.id }
                                  }"
                                  class="i-pill-link"
                                >
                                  {{ buyGet.name }}
                                </router-link>
                              </v-chip>
                            </div>
                          </template>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1 i-data-table"
                    show-expand
                    v-model:expanded="expandedFamilyGroup"
                    :headers="headers"
                    :items="filteredEnrichedFamilyGroups"
                    :page="currentPage"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.imageUrl`]="{ item }">
                      <v-img
                        contain
                        class="family-group-image"
                        :src="item.imageUrl"
                      ></v-img>
                    </template>

                    <template v-slot:[`item.status`]="{ item }">
                      <p
                        v-if="item.status === 'LIVE'"
                        class="g-border-radius-s g-bold i-status-block-green py-2 text-center"
                      >
                        ACTIVE
                      </p>
                      <p
                        v-else
                        class="g-border-radius-s g-bold i-status-block-red py-2 text-center"
                      >
                        INACTIVE
                      </p>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click.stop="editFamilyGroup(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="startDeletion(item)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>

                    <template v-slot:expanded-item="{ headers, item }">
                      <td
                        class="family-group-details py-4 px-8"
                        :colspan="headers.length"
                      >
                        <v-row>
                          <v-col>
                            <p class="g-bold g-heading-s">
                              Detailed information
                            </p>
                          </v-col>
                        </v-row>

                        <v-divider horizontal></v-divider>

                        <v-row>
                          <v-col cols="12" md="6">
                            <div class="pb-2 g-heading-xs g-bold">
                              Products in Family Group
                            </div>
                            <div class="mb-4">
                              <v-chip
                                outlined
                                dense
                                class="mr-1 mb-2"
                                color="primary"
                                v-for="product in item.products"
                                :key="product.productCode"
                              >
                                <router-link
                                  class="i-pill-link"
                                  :to="{
                                    name: 'products',
                                    query: {
                                      supplierItemId: product.supplierItemId
                                    }
                                  }"
                                  >{{ product.tradeItemDescription }}
                                </router-link>
                              </v-chip>
                            </div>
                          </v-col>
                          <v-divider vertical class="ma-4"></v-divider>
                          <v-col cols="12" md="5">
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFuturePriceDrops,
                                  'ACTIVE'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Active Price Drops
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="priceDrop in promoByStatus(
                                    item.activeOrFuturePriceDrops,
                                    'ACTIVE'
                                  )"
                                  :key="priceDrop.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'price-drops',
                                      query: { id: priceDrop.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ priceDrop.startDate }} ~
                                    {{ priceDrop.endDate }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFuturePriceDrops,
                                  'UPCOMING'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Upcoming Price Drops
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="priceDrop in promoByStatus(
                                    item.activeOrFuturePriceDrops,
                                    'UPCOMING'
                                  )"
                                  :key="priceDrop.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'price-drops',
                                      query: { id: priceDrop.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ priceDrop.startDate }} ~
                                    {{ priceDrop.endDate }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFuturePromotions,
                                  'ACTIVE'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Active Specials
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="special in promoByStatus(
                                    item.activeOrFuturePromotions,
                                    'ACTIVE'
                                  )"
                                  :key="special.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'specials',
                                      query: { id: special.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ special.startDate }} ~
                                    {{ special.endDate }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFuturePromotions,
                                  'UPCOMING'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Upcoming Specials
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="special in promoByStatus(
                                    item.activeOrFuturePromotions,
                                    'UPCOMING'
                                  )"
                                  :key="special.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'specials',
                                      query: { id: special.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ special.startDate }} ~
                                    {{ special.endDate }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFutureVolumePromotions,
                                  'ACTIVE'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Active Volume Buys
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="volumeBuy in promoByStatus(
                                    item.activeOrFutureVolumePromotions,
                                    'ACTIVE'
                                  )"
                                  :key="volumeBuy.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'volume-buys',
                                      query: { id: volumeBuy.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ volumeBuy.name }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFutureVolumePromotions,
                                  'UPCOMING'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Upcoming Volume Buys
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="volumeBuy in promoByStatus(
                                    item.activeOrFutureVolumePromotions,
                                    'UPCOMING'
                                  )"
                                  :key="volumeBuy.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'volume-buys',
                                      query: { id: volumeBuy.id }
                                    }"
                                    class="i-pill-link"
                                    >{{ volumeBuy.name }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFutureBuyGetFreePromotions,
                                  'ACTIVE'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Active Buy Get Free
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  small
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="buyGet in promoByStatus(
                                    item.activeOrFutureBuyGetFreePromotions,
                                    'ACTIVE'
                                  )"
                                  :key="buyGet.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'buy-get-free',
                                      query: { id: buyGet.id }
                                    }"
                                    class="i-pill-link"
                                  >
                                    {{ buyGet.name }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>

                            <template
                              v-if="
                                promoByStatus(
                                  item.activeOrFutureBuyGetFreePromotions,
                                  'UPCOMING'
                                ).length
                              "
                            >
                              <div class="pb-2 g-heading-xs g-bold">
                                Upcoming Buy Get Free
                              </div>
                              <div class="mb-4">
                                <v-chip
                                  outlined
                                  dense
                                  small
                                  class="mr-1 mb-2"
                                  color="primary"
                                  v-for="buyGet in promoByStatus(
                                    item.activeOrFutureBuyGetFreePromotions,
                                    'UPCOMING'
                                  )"
                                  :key="buyGet.id"
                                >
                                  <router-link
                                    :to="{
                                      name: 'buy-get-free',
                                      query: { id: buyGet.id }
                                    }"
                                    class="i-pill-link"
                                  >
                                    {{ buyGet.name }}
                                  </router-link>
                                </v-chip>
                              </div>
                            </template>
                          </v-col>
                        </v-row>
                      </td>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import { deleteFamilyGroup } from '@/services/familyGroups'
import { findProductsForSupplierAndProductCodes } from '@/services/products'
import iDeleteFamilyGroup from '@/modules/dataMaintenance/components/DeleteFamilyGroup'
import iEditFamilyGroup from '@/modules/dataMaintenance/components/EditFamilyGroup'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

export default {
  name: 'family-groups',

  components: {
    iEmptyResults,
    iInstructions,
    iDeleteFamilyGroup,
    iEditFamilyGroup,
    iLocalFilter,
    iOverlay
  },

  props: {
    familyGroupId: String
  },

  data: () => ({
    loading: false,
    currentPage: 1,
    emptyFilteredResultsText: 'No Family Groups found',
    editableFamilyGroup: {},
    expandedFamilyGroup: [],
    deletingFamilyGroup: false,
    deletionDialog: false,
    familyGroupDialog: false,
    familyGroupProductsByCode: {},
    filterText: '',
    headers: [
      {
        text: 'FAMILY GROUP IMAGE',
        value: 'imageUrl',
        align: 'center',
        sortable: false,
        width: 200
      },
      { text: 'FAMILY GROUP NAME', value: 'name' },
      { text: 'STATUS', value: 'status', align: 'center', width: 200 },
      { text: 'ACTIONS', value: 'actions', sortable: false, align: 'center' }
    ],
    selectedFamilyGroup: null
  }),

  computed: {
    ...mapState('products', ['products']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('familyGroups', ['familyGroups']),

    enrichedFamilyGroups() {
      return (this.familyGroups || []).map((familyGroup) => ({
        ...familyGroup,
        showDetails: false, // for mobile version expandable
        products: familyGroup.productCodes
          .map((code) => this.getProductByCode(code))
          .filter(Boolean)
      }))
    },

    filteredEnrichedFamilyGroups() {
      return this.enrichedFamilyGroups.filter((familyGroup) =>
        familyGroup.name.toLowerCase().includes(this.filterText.toLowerCase())
      )
    }
  },

  watch: {
    async selectedSupplierId(supplierId, previousSupplierId) {
      if (!supplierId || supplierId === previousSupplierId) return

      await this.getFamilyGroups()
      if (this.familyGroupId) this.expandFamilyGroup(this.familyGroupId)
    }
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),

    // ***
    // Helper functions
    // ***

    expandFamilyGroup(familyGroupId) {
      const fIndex = this.enrichedFamilyGroups.findIndex(
        (f) => f.id === parseInt(familyGroupId)
      )

      this.expandedFamilyGroup = [this.enrichedFamilyGroups[fIndex]]
      this.currentPage = Math.floor(fIndex / this.pageSize) + 1
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    // Used in the expandable section
    promoByStatus(activeOrFuturePromo = [], status) {
      return (activeOrFuturePromo || []).filter(
        (promo) => promo.status === status
      )
    },

    getProductByCode(productCode) {
      return this.familyGroupProductsByCode[productCode]
    },

    // ***
    // Functions related to handling actions
    // ***

    async editFamilyGroup(familyGroup = {}) {
      await this.fetchProducts({
        supplierId: this.selectedSupplierId,
        force: true,
        status: 'LIVE',
        page: 0,
        size: 25
      })

      this.editableFamilyGroup = _.cloneDeep({
        name: '',
        productCodes: [],
        products: [],
        newImage: undefined,
        imageUrl: undefined,
        ...familyGroup
      })
      this.familyGroupDialog = true
    },

    deleteFamilyGroup(familyGroup = {}) {
      this.addNotification({
        color: 'secondary',
        text: 'Deleting Family Group...'
      })
      this.deletingFamilyGroup = true
      deleteFamilyGroup(familyGroup.id)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: `Family Group ${familyGroup.name} has been deleted!`
          })
          this.reload()
        })
        .catch(() => {
          this.addNotification({
            color: 'secondary',
            text: `Family Group ${familyGroup.name} could not be deleted...`
          })
        })
        .finally(() => {
          this.deletingFamilyGroup = false
        })
    },

    reload() {
      this.getFamilyGroups()
      this.reset()
    },

    reset() {
      this.editableFamilyGroup = {}
      this.selectedFamilyGroup = null
      this.familyGroupDialog = this.deletionDialog = false
    },

    startDeletion(item) {
      this.selectedFamilyGroup = item
      this.deletionDialog = true
    },

    // ***
    // Functions related to fetching data
    // ***

    async loadFamilyGroupProducts() {
      const productCodes = [
        ...new Set(
          (this.familyGroups || []).flatMap(
            (familyGroup) => familyGroup.productCodes || []
          )
        )
      ].filter(Boolean)

      if (!productCodes.length) {
        this.familyGroupProductsByCode = {}
        return
      }

      const products = await findProductsForSupplierAndProductCodes(
        this.selectedSupplierId,
        productCodes
      )

      this.familyGroupProductsByCode = products.reduce((acc, product) => {
        acc[product.productCode] = product
        return acc
      }, {})
    },

    async getFamilyGroups() {
      if (!this.selectedSupplierId) {
        this.familyGroupProductsByCode = {}
        return
      }

      try {
        this.loading = true
        await this.fetchFamilyGroups(this.selectedSupplierId)
        await this.loadFamilyGroupProducts()
      } catch {
        this.addNotification({
          color: 'error',
          text: 'Family Groups data could not be loaded, please try again...'
        })
      } finally {
        this.loading = false
      }
    }
  },

  async mounted() {
    if (!this.selectedSupplierId) return

    try {
      this.loading = true
      await this.getFamilyGroups()
      if (this.familyGroupId) this.expandFamilyGroup(this.familyGroupId)
    } catch {
      this.addNotification({
        color: 'error',
        text: 'Family Groups data could not be loaded, please try again...'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#family-groups {
  .family-group-image {
    margin: 4px auto;
    height: auto;
    max-height: 120px;
    width: 80%;
  }
}

@media only screen and (min-width: 960px) {
  #family-groups {
    .family-group-image {
      height: 60px;
      width: 60px;
    }
  }
}
</style>
